import { Component, NgZone, ViewChild } from '@angular/core';
import {
  Nav,
  Platform,
  MenuController,
  App,
  ModalController,
  ToastController,
  Tabs,
  LoadingController,
} from 'ionic-angular';
import { IonicApp } from 'ionic-angular';
import {
  App as AppPlugin,
  AppState,
  URLOpenListenerEvent,
} from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@ionic-native/status-bar';
import { AppMinimize } from '@ionic-native/app-minimize';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { AppVersion } from '@ionic-native/app-version';

import { EnvVariables } from '../environments/environment';
import { ToolsProvider } from '../providers/tools/tools';
import { AuthProvider } from '../providers/auth/auth';
import { UserProvider } from '../providers/user/user';
import { PushNotificationsProvider } from '../providers/push-notifications/push-notifications';
import { ConfigProvider } from '../providers/config/config';
import { GeolocationProvider } from '../providers/geolocation/geolocation';
import { TripService } from '../providers/trip-service/trip-service';
import { MediaSelectorProvider } from '../providers/image-selector/media-selector';
import { CrudApiProvider } from '../providers/crud-api/crud-api';
import { OnboardingProvider } from '../providers/onboarding/onboarding';
import { HintsProvider } from '../providers/hints/hints';
import { NativeAccessProvider } from '../providers/native-access/native-access';

import * as debug from 'debug';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as parse from 'url-parse';
import * as qString from 'query-string';
import { NewsBrowserProvider } from '../providers/news-browser/news-browser';
import { DeepLinkType } from '../providers/tools/tools.types';
import { UserVerificationProvider } from '../providers/user-verification/user-verification';
import { LiveChatProvider } from '../providers/live-chat/live-chat';

let hasHijackedWindowOpen = false;

declare let CmKit: any;

@Component({
  templateUrl: 'app.html',
})
export class MyApp {
  log = debug('app:MyApp');

  @ViewChild(Nav)
  nav: Nav;
  @ViewChild('content') tabRef: Tabs;
  @ViewChild('chatGiveAway') chatGiveAway;

  authenticated = false;
  moderator = false;
  rootPage = 'WelcomePage';
  rootParams: any = {};
  currentPageId = '';
  menuPages: any[] = [];
  notificationCount = 0;
  notificationBellClass = 'custom-notifications';
  userInfo;
  announcement: any = {};
  onLoadFunction;
  hasFocus = true;
  lostFocus: number;
  mapTitle = 'Map';
  banModal;
  updateModal;
  announcementModal;
  announcementModalOpen = false;
  previewMode = false;
  reviewModalOpen = false;
  // reviewModalOpen = true;
  showMainDrawer = false;
  searchOpen = false;

  // no longer have intro popover
  introPopover = false;

  isEnroute = false;
  hasPendingContactAlerts = false;

  newLiveChatMessageIcon = false;
  recentChatId;
  setExpanded = false;

  lastClipboard;

  // bootstrapping variables
  ready = false; // finally ready
  readyAndShowing = false;
  checkedAuthenticated = false;
  platformReady = false;
  gotConfigs = false;
  tabsReady = false;
  removeTabs = false;

  atHomeTop = true;
  deepLinkOpenFn;

  isScrolling = false;
  lastScrollPosition = 0;

  session = {
    start: null,
    end: null,
    interval: null,
    sessionResetInterval: 10000,
    sessionIntervalLimit: 2000,
  };

  subs: any = [];
  lcGiveAwayConfig;

  constructor(
    private zone: NgZone,
    public platform: Platform,
    public statusBar: StatusBar,
    public env: EnvVariables,
    public tools: ToolsProvider,
    public authProvider: AuthProvider,
    public menuCtrl: MenuController,
    public modalCtrl: ModalController,
    public toastCtrl: ToastController,
    private authService: AuthProvider,
    public user: UserProvider,
    public tripService: TripService,
    public pushNotifications: PushNotificationsProvider,
    public configProvider: ConfigProvider,
    public appCtrl: App,
    private appMinimize: AppMinimize,
    public screenOrientation: ScreenOrientation,
    public geolocation: GeolocationProvider, // needed to initialize everything
    public appVersion: AppVersion,
    public crudApi: CrudApiProvider,
    private ionicApp: IonicApp,
    public onboarding: OnboardingProvider,
    public hints: HintsProvider,
    public nativeAccess: NativeAccessProvider, // required to init right away
    public mediaSelector: MediaSelectorProvider,
    public newsBrowser: NewsBrowserProvider,
    public userVerificationProvider: UserVerificationProvider,
    public loadingCtrl: LoadingController,
    public liveChat: LiveChatProvider
  ) {
    this.log('starting app!!!', this.appCtrl, window.location);

    window['__app'] = this;

    this.listenForPushNotification();

    this.startSessionTracking();

    this.tools.logEvent('general', 'openApp');
    this.tools.logEvent('openApp');
    this.tools.mainAppController = this;
    this.listenForFocus();
    this.initializeApp();

    window.addEventListener('resize', () => {
      this.tools.emit('resize');
    });

    platform.ready().then(() => {
      try {
        if (window['cordova']) {
          this.screenOrientation.lock(
            this.screenOrientation.ORIENTATIONS.PORTRAIT
          );
        }
      } catch (e) {}

      try {
        CmKit.utils.updateSettings({
          defaultContainer: document.querySelector('ion-app'),
        });
      } catch (e) {}

      this.hints.configure({
        app: this,
      });

      AppPlugin.addListener('appStateChange', (state: AppState) => {
        if (_.get(state, 'isActive') === true) {
          this.versionModal();
          if (this.authenticated) {
            this.announcementsModal();
          }
        }
      });

      this.platformReady = true;
      this.versionModal();
      this.checkOnReady();
      this.handleBackButton();
      this.handleAppZoom();

      // to launch JobSearchModal for applications
      localStorage.setItem('CDLLIFE_APPLICATION_SUBMITTED', 'false');
      this.setResumeListener();
      this.tools.setAppZoom();

      this.getDeviceId();
    });

    this.tripService.on('enroute', status => {
      if (status) {
        this.mapTitle = 'Active Trip';
        this.isEnroute = true;
        this.log('app isENROUTE');
      } else {
        this.mapTitle = 'Map';
        this.isEnroute = false;
        this.log('not isENROUTE');
      }
      this.log('map title change: ', this.mapTitle, status);
    });

    this.user.on('updated', () => {
      this.userInfo = this.user.get();
      this.log(
        'userInfo after updated going to check walkthrough',
        _.clone(this.user.get()),
        !this.tools.hasSeenIntro('walkThrough')
      );
      this.announcementsModal();
      this.checkBannedStatus();
    });

    this.authService.isAuthenticated().then(
      authed => {
        if (authed) {
          this.authenticated = true;
          this.userInfo = this.user.get();
          this.previewMode = _.get(this.userInfo, 'states.isUnauthedUser');

          this.moderator = this.user.isModerator();
          this.log('moderator: ', this.moderator);
          this.setUserAppVersion(this.user.get());

          if (!this.introPopover) {
            this.readyForApp();
          }
        } else {
          this.authModal();
          this.authenticated = false;
          this.previewMode == true;
        }

        this.tools.logAnalyticsEvent('track', 'ViewContent');

        this.checkedAuthenticated = true;
        this.checkOnReady();
      },
      () => {
        this.authModal();
        this.checkedAuthenticated = true;
        this.checkOnReady();
      }
    );

    this.configProvider
      .loadConfigs()
      .catch(() => {
        this.log('error catch in onReady()');
        return Promise.resolve();
      })
      .then(async () => {
        this.log('got configs');
        this.gotConfigs = true;
        
        if (this.configProvider.get('sponsorship-live-chat-giveaway')) {
          this.lcGiveAwayConfig = this.configProvider.get(
            'sponsorship-live-chat-giveaway'
          );
          const cId = _.get(this.lcGiveAwayConfig, 'advertiser.conversationId'); 
          const hasJoined = await this.liveChat.hasJoinedRoom(cId);
          const hasIgnoredRoomContest = await this.liveChat.hasIgnoredRoomContest(cId);

          if (!hasJoined && !hasIgnoredRoomContest) {
            setTimeout(() => {
              this.chatGiveAway.show();
            }, 500);
          }
        }

        if (this.configProvider.get('home-scroll-menus-hiding')) {
          this.subs.push(
            this.tools.on('isScrollingFeed', isScrolling => {
              this.isScrolling = isScrolling;
            })
          );
          this.subs.push(
            this.tools.on('forceMenusShow', e => {
              this.isScrolling = false;
            })
          );
        }
        this.checkOnReady();
      });

    this.notificationCount = this.pushNotifications.getCount();
    this.pushNotifications.on('update', () => {
      this.notificationCount = this.pushNotifications.getCount();
      if (this.notificationCount > 0) {
        this.notificationBellClass = 'custom-notifications-pending';
      } else {
        this.notificationBellClass = 'custom-notifications';
      }
      this.log('updated notification count', this.notificationCount);
    });

    this.authProvider.on('authenticated', () => {
      this.authenticated = true;
      this.userInfo = this.user.get();
      this.moderator = this.user.isModerator();
      this.setUserAppVersion(this.user.get());
      this.introPopover = false;
      this.checkReadyAndShowing();
    });

    this.authProvider.on('logout', () => {
      this.authenticated = false;
      this.moderator = false;
    });
  }

  private authModal() {
    // prevents background from showing up before onboarding slides in
    this.introPopover = true;
    setTimeout(() => {
      this.introPopover = false;
    }, 500);

    this.onboarding.startOnboarding().then(() => {
      this.authProvider.settings.autoGenerateUnauthedUser = true;
      this.authProvider.checkingAuth = null;
      this.authProvider
        .isAuthenticated()
        .catch(e => {
          // stifle
        })
        .then(() => {
          this.introPopover = false;
          //this.showHints();
          this.readyForApp();

          // forces home page to render without preview mode race conditions
          window.location.reload();
        });
    });
  }

  private announcementsModal() {
    if (this.user.userAccountAge() <= 5) return;

    if (this.announcementModalOpen) return;
    this.announcementModalOpen = true;

    let q = {
      announcementType: 'major',
      limit: 1,
    };

    this.crudApi.get('/api/announcements', q).then(
      res => {
        if (_.isEmpty(res.data)) {
          this.announcementModalOpen = false;
          return;
        }

        this.announcement = res.data[0];
        if (
          localStorage.getItem('ANNOUNCEMENT_MAJOR_VIEWED') !==
          _.get(this.announcement, '_id')
        ) {
          setTimeout(() => {
            if (!this.announcementModal) {
              this.announcementModal = this.modalCtrl.create(
                'AnnouncementsPage',
                {
                  announcements: this.announcement,
                }
              );
              this.announcementModal.onDidDismiss(() => {
                this.announcementModal = null;
                if (!this.announcement.adminPreview) {
                  localStorage.setItem(
                    'ANNOUNCEMENT_MAJOR_VIEWED',
                    _.get(this.announcement, '_id')
                  );
                }
                this.announcementModalOpen = false;
              });
              this.announcementModal.present();
            } else if (this.announcementModal) {
              this.announcementModal.dismiss();
              this.announcementModal = null;
              this.announcementModalOpen = false;
            }
          });
        } else {
          this.announcementModalOpen = false;
        }
      },
      e => {
        this.announcementModalOpen = false;
      }
    );
  }

  private versionModal() {
    setTimeout(() => {
      this.getAppVersionAndVersionRequirements().then(promisesRes => {
        const version = _.head(promisesRes), // current app version (or faked version)
          versionControl = _.last(promisesRes), // min app versions config
          skippedVersion = localStorage.getItem('SKIPPED_VERSION'); // skipped version
        const isAppCurrent =
          this.tools.isAppVersionNewerThanSecondVersion(
            version,
            _.get(versionControl, 'normalVersionNumber')
          ) || version === _.get(versionControl, 'normalVersionNumber');

        let hasUserSkippedCurrentVersion =
          skippedVersion &&
          skippedVersion === _.get(versionControl, 'normalVersionNumber');

        // user has skipped, but now we are forcing this same version
        if (skippedVersion === _.get(versionControl, 'forcedVersionNumber')) {
          hasUserSkippedCurrentVersion = false;
        }

        if (
          !isAppCurrent &&
          !this.updateModal &&
          !hasUserSkippedCurrentVersion
        ) {
          this.updateModal = this.modalCtrl.create('UpdateAppPage', {
            installed: version,
            versionControl: versionControl,
          });
          this.updateModal.onDidDismiss(() => {
            this.updateModal = null;
            this.versionModal();
          });
          this.updateModal.present();
        } else if (isAppCurrent && this.updateModal) {
          this.updateModal.dismiss();
          this.updateModal = null;
        }
      });
    });
  }

  private getAppVersionAndVersionRequirements() {
    return Promise.all([
      localStorage.getItem('FAKED_VERSION')
        ? Promise.resolve(localStorage.getItem('FAKED_VERSION'))
        : this.appVersion.getVersionNumber(),
      this.getConfigVersion(),
    ]).then(res => {
      return res;
    });
  }

  private getConfigVersion() {
    return new Promise((resolve, reject) => {
      this.crudApi
        .get('/api/configs')
        .then(res => {
          this.log('res config', res);
          const versionControl = _.get(res, 'data.versionControl');
          resolve(versionControl);
        })
        .catch(error => {
          reject();
        });
    });
  }

  private async listenForPushNotification() {
    this.log('pushNotification | setting listener');
    this.pushNotifications.on('open', n => {
      const loading = this.loadingCtrl.create(
        this.tools.loadingMessageIfAdmin('notification open emitted ...')
      );
      loading.present();
      this.log('pushNotification | listener');
      this.introPopover = false;
      this.checkReadyAndShowing();

      try {
        this.closeAllModals();
        setTimeout(async () => {
          if (!this.ready) {
            this.onLoadFunction = async () => {
              this.log('onLoadFunction() | running from pushNotification open');
              try {
                loading.dismiss();
                loading.dismissAll();
              } catch (e) {}
              await new Promise(resolve => setTimeout(resolve, 250));
              this.tools.openNotification(n, this.tabRef.getSelected());
            };
          } else {
            this.log('onLoadFunction() |trying push notif open if not ready');
            try {
              loading.dismiss();
              loading.dismissAll();
            } catch (e) {}
            await new Promise(resolve => setTimeout(resolve, 250));
            this.tools.openNotification(n, this.tabRef.getSelected());
          }
        }, 10);
      } catch (e) {
        this.log('error here with opening notifications');
      }
    });
  }

  private async deepLinks() {
    AppPlugin.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        const slug = event.url.split('.app').pop();

        this.log('deepLink url | ', slug);

        let pathNameSplit = _.split(parse(slug).pathname, '/');
        const qs = qString.parseUrl(slug);
        const dlType = pathNameSplit[1];
        const dlSub = pathNameSplit[2];
        const dlSubdomain = dlSub === '-' ? '' : `${dlSub}.`;
        const dlHost = `https://${dlSubdomain}cdllife.com`;
        const dl = this.tools.getDeepLinkConfig(dlType);
        const subsequentParams = _.split(
          _.split(slug, '?')[0],
          `/dl/${dlSub}/`
        )[1];
        const externalUtms = _.merge(
          {
            rt: 'mobileApp',
            utm_source: 'cbo-cdllife-mobileApp',
            utm_campaign: `open-external`,
            utm_medium: 'app-dl',
            mTKAuth: localStorage.getItem('APP_AUTH_KEY'),
          },
          qs.query
        );
        let utms = {
          utm_campaign: 'app-dl',
          utm_term: dl ? dl.component : _.split(slug, '?')[1],
          utm_content: JSON.stringify(subsequentParams),
        };

        if (qs.query) {
          const utmCodes = [
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_term',
            'utm_content',
          ];

          _.each(utmCodes, code => {
            const u = _.get(qs.query, code);
            if (_.get(qs.query, code)) {
              _.set(utms, code, u);
            }
          });
        }

        this.tools.logAnalyticsEvent('trackCustom', 'deep-link-open', {
          utms,
        });

        if (dl) {
          await this.closeAllModals();
          await this.tabRef.select(dl.tab || 0);

          if (dlType === 'dl') {
            this.log('deepLinks | external link', slug);
            if (dlSubdomain === 'app.' || dlSubdomain === 'm.') {
              this.log('deepLinks | internal post', slug);
              const dlSubType = pathNameSplit[3];
              const d = this.tools.getDeepLinkConfig(dlSubType);
              await this.internalDeepLink(d, pathNameSplit);
            } else {
              await this.externalDeepLink(
                dlHost,
                subsequentParams,
                externalUtms
              );
            }
          } else {
            this.log('deepLinks | internal post', slug);
            delete pathNameSplit[0];
            pathNameSplit = _.compact(pathNameSplit);
            pathNameSplit.unshift('m');
            pathNameSplit.unshift('dl');
            pathNameSplit.unshift('/');
            await this.internalDeepLink(dl, pathNameSplit);
          }

          // maybe, not using now, don't know if this would even be helpful
          // if (_.get(dl, 'function')) {
          //   this.log('deepLinks | function');
          //   await this[dl.function]();
          // }
        } else {
          this.log('deepLink | invalid dl');
        }
      });
    });
  }

  async getQueryParams(search: string) {
    return JSON.parse(
      '{"' +
        decodeURI(search)
          .replace(/"/g, '\\"')
          .replace(/&/g, '","')
          .replace(/=/g, '":"') +
        '"}'
    );
  }

  async externalDeepLink(host, params, query) {
    let externalLink = `${host}/${params === '-' ? '' : params}`;
    let mobileArticle = null;
    this.log('deepLinks | external', host, params, query);
    if (host === 'https://cdllife.com' && _.get(query, 'newsBrowser')) {
      // open internal article if possible
      delete query.newsBrowser;
      mobileArticle = await this.crudApi
        .get('/api/posts/articleUrl', {
          url: externalLink,
        })
        .catch(e => {
          this.log('deepLinks | e', e);
          this.tools.openExternalUrl(externalLink, 'external', query);
        });
    }
    if (mobileArticle) {
      this.log('deepLinks | cdllife article', mobileArticle.data);
      return this.newsBrowser.openArticle(mobileArticle.data);
    } else {
      this.log('deepLinks | external link', externalLink);
      this.tools.openExternalUrl(externalLink, 'external', query);
    }
  }

  async internalDeepLink(dl: DeepLinkType, pathNameSplit) {
    this.log('deepLinks | internal', pathNameSplit, dl);
    // const newToast = this.toastCtrl.create({
    //   message: JSON.stringify(pathNameSplit),
    //   position: 'bottom',
    //   duration: 5000,
    // });
    // newToast.present();
    if (pathNameSplit[3] === 'verificationSubmitted') {
      this.userVerificationProvider.setPendingVerificationStatus();
      const toast = this.toastCtrl.create({
        message: 'Your verification request has been received.',
        position: 'top',
        duration: 5000,
      });
      toast.present();
      this.tools.navTo('UserProfilePage', null, {
        user_id: this.user.get('_id'),
      });
    } else {
      await this.tabRef.select(dl.tab);
      const nav = this.tabRef.getActiveChildNavs()[0];
      let query = {};
      if (dl.params) {
        _.each(dl.params, (param, index) => {
          query[param] = pathNameSplit[index + 4]; // skip 0/dl/subdomain/page indexes
        });
      }
      this.log('deepLinks | internal query', JSON.stringify(query));
      nav.push(dl.component, query);
    }
  }

  oldDeepLinkFn(slug) {
    // saved the old stuff
    const match = String(slug).match(/post\/([a-z0-9]+)/i),
      args = match
        ? {
            post_id: match[1],
          }
        : null;

    this.log('deepLinks | match', match);

    // NOTE: If we start making cdllife.com articles a part of this
    // we will need more logic to handle which page to go to

    if (_.get(args, 'post_id') === this.lastClipboard) {
      this.log('deepLinks | not routing');
      return;
    }

    this.lastClipboard = _.get(args, 'post_id');

    const fn = () => {
      this.closeAllModals().then(() => {
        this.tabRef.select(0).then(
          () => {
            const nav = this.tabRef.getActiveChildNavs()[0];
            nav.push('CommunityPostsPage', args);
            setTimeout(() => {
              this.lastClipboard = null;
            }, 5000);
          },
          e => {
            console.error('deepLinkOpenFn() | pop to root error', e);
          }
        );
      });
    };
    if (this.readyAndShowing) {
      this.log('deepLinks | running fn');
      fn();
    } else {
      this.log('deepLinks | setting deep link');
      this.deepLinkOpenFn = fn;
    }
  }

  tabClicked(e) {
    try {
      const el = e.target;
      let tab;

      if (el.classList.contains('tab-button')) {
        tab = el;
      } else {
        tab = el.closest('.tab-button');
      }

      if (!tab) return;

      if (
        tab.getElementsByClassName('tab-button-text').item(0).innerText ===
        'Jobs'
      ) {
        this.tools.openJobs(this.user.get('_id'), 'main-nav');
      }

      if (tab.getAttribute('aria-selected') !== 'true') return;

      const nViews = _.size(this.tabRef.getSelected()._views);

      if (nViews === 1) {
        this.tabRef.getSelected().goToRoot({});
      }
    } catch (err) {
      this.log('err on tab click', err);
    }
    // try {
    //   const root = this.tabRef.getSelected().root,
    //     nViews = _.size(this.tabRef.getSelected()._views);
    //   this.log('tabs clicked %s vs %s, views(%s)', tab, root, nViews);
    // } catch (e) {
    //   this.log('tab error', e);
    // }
  }

  tabChanged($ev) {
    // Setting Root Page everytime TAB changed
    // Will prevent heading back to previous pushed page TAB had opened
    // Instead will reset to the root
    // EX: clicking 'More' TAB, click navigating to 'My Profile',
    // then clicking a different TAB, and clicking back to the 'More' TAB
    // $ev.setRoot($ev.root);

    this.log('tabs set ready', this.tabsReady, $ev.root);

    this.tools.emit('closePreviewModal');

    if ($ev.root === 'HomePage') {
      setTimeout(() => {
        this.tools.emit('refresh-announcements');
      });
    } else {
      this.user.setActivatedUserStatus();
    }

    if ($ev.root === 'ChatsHomePage') {
      this.newLiveChatMessageIcon = false;
    }

    // tracking tab changes
    // removing 'Page' from name
    const fbEventPageName = _.split(_.toLower(_.toString($ev.root)), 'page');
    // changing plan-a-route page name to maps for tracking
    if (fbEventPageName[0] === 'planaroute') {
      fbEventPageName[0] = 'map';
    } else if (fbEventPageName[0] === 'home') {
      fbEventPageName[0] = 'feed';
    } else if (fbEventPageName[0] === 'settings') {
      fbEventPageName[0] = 'more';
    }

    this.tools.logAnalyticsEvent('trackCustom', 'tabs-' + fbEventPageName[0]);

    this.tabsReady = true;
  }

  private handleWelcomeScreen() {
    this.rootPage = 'WelcomePage';
    let timeout;

    if (!this.introPopover) {
      return;
    }

    this.pushNotifications.once('open', () => {
      clearTimeout(timeout);
      this.introPopover = false;
      this.checkReadyAndShowing();
    });
  }

  private onReadyAndShowing() {
    // Step 1: handle event for if app was open from a push notification
    this.log('onReadyAndShowing()');
    if (this.onLoadFunction) {
      this.log('onReadyAndShowing() | trying onLoadFunction()');
      try {
        this.onLoadFunction();
        setTimeout(() => {
          this.onLoadFunction = null;
        });
      } catch (e) {}
    }

    // Step 2: check deep links
    if (this.deepLinkOpenFn) {
      this.log('onReadyAndShowing() | setTimeoutDeeplink()');
      setTimeout(() => {
        this.log('onReadyAndShowing() | running setTimeoutDeeplink()');
        try {
          window['_deepLinkOpenFn'] = (window['_deepLinkOpenFn'] || 0) + 1;
          this.deepLinkOpenFn();
          this.deepLinkOpenFn = null;
        } catch (e) {}
      }, 1500);
    }
  }

  private checkReadyAndShowing() {
    this.log('checkReadyAndShowing()');
    const wasReady = this.readyAndShowing;
    this.readyAndShowing = this.ready && !this.introPopover;

    if (!wasReady && this.readyAndShowing) {
      this.onReadyAndShowing();
      window['_inReady'] = (window['_inReady'] || 0) + 1;
    }
  }

  private checkOnReady() {
    const wasReady = this.ready;
    this.ready =
      this.checkedAuthenticated && this.platformReady && this.gotConfigs;

    if (!wasReady && this.ready) {
      this.onReady();
    }

    this.checkReadyAndShowing();
  }

  private async setUserAppVersion(userInfo) {
    if (!userInfo) {
      return;
    }
    // check and update app Version
    this.log('checking version number', userInfo, this.crudApi);
    const advertisingId = await this.getDeviceId();
    setTimeout(() => {
      this.appVersion.getVersionNumber().then(
        versionNumber => {
          // force crud update user with version number;
          this.log('version number', versionNumber);
          let update = {
            metadata: {
              appVersion: versionNumber,
            },
          };
          if (advertisingId) {
            _.set(update, 'advertisingId', advertisingId);
          }

          this.crudApi
            .put('/api/users/' + _.get(userInfo, '_id'), update)
            .then(userSuccess => {
              this.log('updated user app version', userSuccess);
            })
            .catch(e => {
              this.log('failed user app version update', e);
            });
        },
        error => {
          this.log(error);
        }
      );
    }, 10000);
  }

  private onReady() {
    this.log('READY!');

    try {
      SplashScreen.hide();
    } catch (e) {
      // console.error('error with splash', e);
    }

    // removing overlayWebView - only need to color the status bar correctly
    // this.statusBar.styleLightContent();
    this.statusBar.backgroundColorByHexString('#ffffff');

    // step 1: handle welcome screen
    this.handleWelcomeScreen();

    // step 2: get configs
    if (!hasHijackedWindowOpen) {
      hasHijackedWindowOpen = true;
      if (window['cordova']) {
        let wo = window.open,
          self = this;
        window['originalOpen'] = wo;
        window['open'] = function (src, target, opts) {
          if (opts || !/^http/.test(src)) {
            return wo.apply(this, arguments);
          } else {
            self.tools.openExternalUrl(src);
          }
        };
      }
    }

    // Step 3: Listen for all nav events
    this.setGlobalNavEvents();
    this.checkClipboard();
  }

  ngOnInit() {
    this.user.on('updated', () => {
      this.userInfo = this.user.get();
      this.getUpdatedLiveChatRooms();
    });
    setTimeout(() => {
      this.getUnReadLiveChatCounts();
    }, 500);
    this.tools.on('pending-contact-updated', () => {
      this.hasPendingContactAlerts = this.user.hasPendingContactAlerts();
    });

    this.liveChat.on('new-live-chat-message', data => {
      this.recentChatId = data.data.userId;

      if (_.get(this.user, 'info._id') !== this.recentChatId) {
        this.newLiveChatMessageIcon = true;
      }
    });

    this.tools.on('close-main-drawer', () => {
      setTimeout(() => {
        this.showMainDrawer = false;
      });
    });

    this.tools.on('open-main-drawer', () => {
      this.showMainDrawer = true;
    });

    this.hints.tabBarReady.subscribe(() => {
      this.showHints();
    });

    this.tools.on('open-review-modal', () => {
      this.reviewModalOpen = true;
    });

    this.tools.on('close-review-modal', () => {
      this.reviewModalOpen = false;
    });

    this.subs.push(
      this.tools.on('searchOpen', e => {
        this.searchOpen = true;
      })
    );
    this.subs.push(
      this.tools.on('searchClose', e => {
        this.searchOpen = false;
      })
    );
  }

  getUpdatedLiveChatRooms() {
    const key = 'recently-updated-chats';
    const storedData = localStorage.getItem(key);
    let chatArray: string[] = storedData ? JSON.parse(storedData) : [];

    if (this.recentChatId !== this.user.info._id) {
      this.newLiveChatMessageIcon = chatArray.length > 0;
    }
  }

  getUnReadLiveChatCounts() {
    this.liveChat.getPublicRooms().then(publicChats => {
      const userId = this.user.info._id;
      let rooms = publicChats;

      _.each(
        _.sortBy(rooms, r => _.size(r?._onlineUsers)),
        rm => {
          if (
            rm.expandable &&
            !this.setExpanded &&
            _.size(rm.messages) >= 5 &&
            !rm.isClosed
          ) {
            this.setExpanded = true;
            rm.expandChat = true;
          }
        }
      );

      rooms = _.sortBy(
        _.filter(rooms, rm => !rm.expandChat),
        'isClosed',
        'desc'
      );

      const simplifiedRooms = rooms.map(room => {
        return {
          socketId: room.socketId,
          unreadCounts: room.unreadCounts,
          isClosed: room.states.isClosed,
        };
      });

      const filteredSimplifiedRooms = simplifiedRooms
        .map(room => {
          const matchingUnreadCounts: Record<string, number> = {};

          for (const key in room.unreadCounts) {
            if (key === userId && room.unreadCounts[key] > 0) {
              matchingUnreadCounts[key] = room.unreadCounts[key];
            }
          }

          for (const key in matchingUnreadCounts) {
            if (matchingUnreadCounts[key] > 0 && !this.newLiveChatMessageIcon) {
              this.newLiveChatMessageIcon = room.socketId;
            }
          }

          if (Object.keys(matchingUnreadCounts).length > 0) {
            const key = 'recently-updated-chats';

            const storedData = localStorage.getItem(key);

            let chatArray: string[] = storedData ? JSON.parse(storedData) : [];

            let socketIdExists = false;
            for (const socketId of chatArray) {
              if (socketId === room.socketId) {
                socketIdExists = true;
                break;
              }
            }

            if (!socketIdExists && !room.isClosed) {
              chatArray.push(room.socketId);
              localStorage.setItem(key, JSON.stringify(chatArray));
            }
            return {
              socketId: room.socketId,
              unreadCounts: matchingUnreadCounts,
            };
          }

          return null;
        })
        .filter(room => room !== null);
    });
  }

  closeDrawer(event) {
    try {
      event.stopPropagation();
    } catch (e) {}

    setTimeout(() => {
      this.tools.emit('close-main-drawer');
    });
  }

  checkBannedStatus() {
    const isBanned = this.user.get('states.isBanned');
    const expiration = this.user.get('dates.banExpiration');

    if (isBanned && !this.banModal) {
      this.banModal = this.modalCtrl.create(
        expiration ? 'BannedUserTimedPage' : 'BannedUserPage',
        {
          user: this.user,
          user_id: this.user.info._id,
        }
      );
      this.banModal.onDidDismiss(() => {});
      this.banModal.present();
    } else if (!isBanned && this.banModal) {
      this.banModal.dismiss();
      this.banModal = null;
    }
  }

  private setGlobalNavEvents() {
    this.log('setGlobalNavEvents() | setting global events');

    setTimeout(() => {
      this.appCtrl.viewDidEnter.subscribe(data => {
        if (data && data.name === 'LoadingCmp') return;

        this.tools.emit('viewDidEnter', data);
        if (!data?.isOverlay) {
          this.currentPageId = data?.component?.name || data.id;
        }
      });

      this.appCtrl.viewWillEnter.subscribe(data => {
        if (!data?.isOverlay) {
          this.currentPageId = data?.component?.name || data.id;
        }

        if (data && data.name === 'LoadingCmp') return;
        this.tools.pauseAllVideos();
        this.log('viewWillEnter', data);
        this.tools.emit('viewWillEnter', data);
        this.tools.emit('closePreviewModal');
      });

      // just want to make sure it's on native
      if (window['cordova']) {
        Keyboard.addListener('keyboardWillShow', data => {
          document.body.classList.add('keyboard-is-open');
          this.tools.emit('onKeyboardShow', data);
        });
        Keyboard.addListener('keyboardDidShow', data => {
          this.tools.emit('onKeyboardDidShow', data);
        });
        Keyboard.addListener('keyboardWillHide', () => {
          document.body.classList.remove('keyboard-is-open');
          this.tools.emit('onKeyboardHide');
        });
        Keyboard.addListener('keyboardDidHide', () => {
          this.tools.emit('onKeyboardDidHide');
          setTimeout(() => {
            Keyboard.setAccessoryBarVisible({ isVisible: true });
          });
        });
      }

      window.addEventListener('orientationchange', () => {
        this.tools.emit(
          'orientationChange',
          _.get(window, 'screen.orientation.type')
        );
      });

      this.deepLinks();
    });
  }

  private initializeApp() {
    this.platform.ready().then(() => {
      this.statusBar.styleDefault();
      this.pushNotifications.init();

      try {
        // just want to make sure it's on native
        if (window['cordova']) {
          Keyboard.setAccessoryBarVisible({ isVisible: true });
        }
      } catch (e) {}
    });
  }

  private showHints() {
    if (!this.previewMode) {
      return;
    }

    this.hints
      .hintWorkflow('TABBAR')
      .onVisit(0, [
        {
          selector: '#tab-t0-0',
          text: 'Interact with the CDLLife driver community. Share trucking links, photos, videos, GIFs, and polls.',
          forceCircle: true,
          offset: {
            y: -6,
          },
          arrow: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        },
        {
          selector: '#tab-t0-1',
          text: 'See your contacts, send DMs, create Meet & Greets, and discover nearby friends.',
          forceCircle: true,
          offset: {
            y: -6,
          },
          arrow: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        },
        {
          selector: '#tab-t0-2',
          text: 'Explore new app features and check out CDLLife Products.',
          forceCircle: true,
          offset: {
            y: -6,
          },
          arrow: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        },
        {
          selector: '#tab-t0-2',
          text: 'Use our trucking tools & services. Find parking, plan a trip, view jobs, and more.',
          forceCircle: true,
          offset: {
            y: -6,
          },
          arrow: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        },
        {
          selector: '#tab-t0-4',
          text: 'View your notifications',
          forceCircle: true,
          offset: {
            y: -6,
          },
          arrow: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        },
      ])
      .run({ quickDelay: 500, delay: 1e3 });
  }

  private closeAllModals() {
    this.log('closeAllModals()');
    try {
      return this.appCtrl
        .getActiveNav()
        .popAll()
        .catch(e => {
          return Promise.resolve();
        });
    } catch (e) {
      this.log('error in closing modals', e);
      return Promise.resolve();
    }
  }

  private listenForFocus() {
    setTimeout(() => {
      window.addEventListener('blur', () => {
        this.lostFocus = new Date().getTime();
        this.hasFocus = false;
      });

      window.addEventListener('focus', () => {
        this.hasFocus = true;
        if (!this.session.interval) {
          this.startSessionTracking();
          this.lostFocus = null;
        }
      });
    });
  }

  private handleBackButton() {
    this.log('BACKBUTTON | going to handle back button');
    this.platform.backButton.subscribe(e => {
      this.log('BACKBUTTON | Back button action called');

      const ionicApp: any = this.ionicApp,
        menuCtrl: any = this.menuCtrl;

      const activeLoading = ionicApp._loadingPortal.getActive();
      const activeModal = ionicApp._modalPortal.getActive();
      const activeToast = ionicApp._toastPortal.getActive();
      const activeOverlay = ionicApp._overlayPortal.getActive();

      const activePortal =
        activeOverlay || activeLoading || activeModal || activeToast;
      if (activePortal) {
        this.log('BACKBUTTON | activePortal nav', activePortal.getNav());
        let modalNav = activePortal.getNav();
        let canGoBack = modalNav.canGoBack();
        this.log('BACKBUTTON | handled with modal');

        if (modalNav && canGoBack) {
          this.log('BACKBUTTON | modal dismissing itself');
        } else {
          this.log('BACKBUTTON | modalNav.pop()');
          try {
            modalNav.pop();
          } catch (e) {
            this.log('BACKBUTTON | error with modal navs pop', e);
          }
        }

        return;
      }
      this.log('BACKBUTTON | menu Contrl', menuCtrl.isOpen());
      if (menuCtrl.isOpen()) {
        menuCtrl.close();

        this.log('BACKBUTTON | closing menu');
        return;
      }

      let nav = this.appCtrl.getActiveNavs()[0];
      let view = nav.getActive();
      let page = view ? view.instance : null;
      this.log('BACKBUTTON | nav view page', nav, view, page);
      if (page && page.isRootPage && this.atHomeTop) {
        this.log('BACKBUTTON | Handling back button on a home page');
        this.appMinimize.minimize();
      } else if (page && page.isRootPage && !this.atHomeTop) {
        // scrollTop home page
        this.tools.emit('scrollTopHome');
      } else if (nav.canGoBack() || (view && view.isOverlay)) {
        this.log('BACKBUTTON | popping back');
        // don't need nav.pop() ionic handles canGoBack()
        // nav.pop();
      } else {
        this.log('BACKBUTTON | handle tab return to home page', e);

        this.log(
          'BACKBUTTON | checking PlanARoute Page slider - view.id, picker, slider',
          view.id,
          _.get(page, 'showPicker'),
          _.get(page, 'showSlider')
        );
        if (
          view.id === 'PlanARoutePage' &&
          (page.showPicker || page.showSlider)
        ) {
          // close picker/slider
          this.log('closing places sliders');
          this.tools.emit('closePlacesPickerSlider');
        } else {
          try {
            const regexPage = new RegExp('t');
            const regexModal = new RegExp('n');

            this.log(
              'testing if page or modal',
              nav.id,
              regexPage.test(nav.id),
              regexModal.test(nav.id)
            );
            if (regexPage.test(nav.id) === true && !regexModal.test(nav.id)) {
              this.log('page');
              nav.parent.select(0);
            } else {
              // should have already handled modals, menus above, should never get here
              this.log('not a page');
              try {
                nav.pop();
              } catch (e) {
                this.log('error', e);
              }
            }
          } catch (e) {
            this.log('error', e);
          }
        }
      }
    });
  }

  private readyForApp() {
    window['READY_FOR_APP'] = true;
    this.tools.emit('readyForApp');
  }

  private handleAppZoom() {
    const MobileAccessibility: any = window['MobileAccessibility'];
    const Screensize: any = _.get(window['plugins'], 'screensize');

    if (!MobileAccessibility || !Screensize) {
      return;
    }

    Screensize.get(
      result => {
        this.log('handleAppZoom | setting density size', result);

        let zoom = 100; // percent
        const densityBucket = _.get(result, 'densityBucket');

        if (densityBucket === 'xhdpi') {
          // normal allow
          // allowing for smaller text as well
        } else if (densityBucket === 'xxhdpi') {
          zoom = 90; // large
        } else if (densityBucket === 'xxxhdpi') {
          zoom = 80; // xl
        }

        MobileAccessibility.usePreferredTextZoom(false);
        MobileAccessibility.setTextZoom(zoom, res => {
          this.log('handleAppZoom | set size', res);
        });
      },
      err => {
        this.log('handleAppZoom | ERROR', err);
      }
    );
  }

  private setResumeListener() {
    document.addEventListener('resume', res => {
      this.log('resume called', res);

      // TODO: check clipboard
      this.checkClipboard();
    });
  }

  private checkClipboard() {
    if (window['cordova']) {
      const clipboard = _.get(window['cordova'], 'plugins.clipboard');
      // if (clipboard) {
      //   clipboard.paste(text => {
      //     const postId = this.tools.getPostIdfromUrl(text);

      //     // we already went there
      //     if (postId === this.lastClipboard) {
      //       return;
      //     }

      //     this.lastClipboard = postId;
      //     if (postId) {
      //       clipboard.clear();
      //       this.tools.navigateToPost(postId, this.appCtrl.getActiveNav());

      //       setTimeout(() => {
      //         this.lastClipboard = null;
      //       }, 5000);
      //     }
      //   });
      // }
    }
  }

  private getDeviceId() {
    return new Promise((resolve, reject) => {
      if (window['cordova']) {
        try {
          window['cordova'].plugins['idfa'].getInfo().then(function (info) {
            if (!info.limitAdTracking) {
              const id = info.idfa || info.aaid || '';
              resolve(id);
            }
          });
        } catch (e) {
          resolve('');
        }
      } else {
        resolve('');
      }
    }).then(d => {
      if (d) {
        window['_advertisingId'] = d;
      }
      return d;
    });
  }

  setSessionIntervals() {
    this.session.sessionResetInterval =
      this.configProvider.get('sessionResetInterval') || 10000;
    this.session.sessionIntervalLimit =
      this.configProvider.get('sessionIntervalLimit') || 2000;
  }

  startSessionTracking() {
    this.setSessionIntervals();
    this.session.start = moment().format();
    // this.log('session | start: ', this.session.start);
    this.session.interval = setInterval(() => {
      if (!this.hasFocus) {
        const timeCheck = new Date().getTime();
        if (
          timeCheck - this.lostFocus >= this.session.sessionResetInterval &&
          this.lostFocus &&
          this.session.start
        ) {
          this.session.end = moment(this.lostFocus).format();
          if (
            moment(this.session.end).diff(this.session.start) >
            this.session.sessionIntervalLimit
          ) {
            // this.log(
            //   'session | TRACK and RESET: ',
            //   moment(this.session.end).diff(this.session.start)
            // );

            this.tools.logAnalyticsEvent('trackCustom', 'sessionTime', {
              start: this.session.start,
              end: this.session.end,
              length: moment(this.session.end).diff(this.session.start),
            });

            this.lostFocus = null;
            this.session.start = null;
            this.session.end = null;
          } else {
            // this.log('session | RESET ', this.session.sessionIntervalLimit);
            this.lostFocus = null;
            this.session.start = null;
            this.session.end = null;
          }
        } else {
          // this.log('session | waiting');
        }
      } else {
        if (!this.session.start) {
          this.log('session | start new session');
          this.session.start = moment().format();
          this.session.end = null;
        } else {
          // this.log('session | running', moment().diff(this.session.start));
        }
      }
    }, 1000);
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts